import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loan, RulesResult, LOAN_STATUSES } from '../types';
import { useAuthContext } from '../App';
import api from '../api';
import NavBar from '../components/NavBar';
import LoanForm from '../components/LoanForm';
import RatioPanel from '../components/RatioPanel';
import ExceptionPanel from '../components/ExceptionPanel';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  '01-Loan Modeling': { label: '01 Modeling', color: 'bg-gray-700 text-gray-300' },
  '02-Initial Underwriting': { label: '02 Initial UW', color: 'bg-[#7C3AED]/20 text-[#A78BFA]' },
  '03-Underwriting': { label: '03 Underwriting', color: 'bg-[#2563EB]/20 text-[#60A5FA]' },
  '04-Approval': { label: '04 Approval', color: 'bg-[#0891B2]/20 text-[#22D3EE]' },
  '05-Closing': { label: '05 Closing', color: 'bg-[#D97706]/20 text-[#FCD34D]' },
  '06-Boarded': { label: '06 Boarded', color: 'bg-[#059669]/20 text-[#34D399]' },
  '07-Servicing': { label: '07 Servicing', color: 'bg-[#10B981]/20 text-[#6EE7B7]' },
  declined: { label: 'Declined', color: 'bg-[#EF4444]/20 text-[#F87171]' },
};

// Step-by-step progression bar for the loan detail header
function StatusProgressBar({ currentStatus }: { currentStatus: string }) {
  const steps = [
    '01-Loan Modeling',
    '02-Initial Underwriting',
    '03-Underwriting',
    '04-Approval',
    '05-Closing',
    '06-Boarded',
    '07-Servicing',
  ];

  if (currentStatus === 'declined') {
    return (
      <div className="flex items-center gap-2 mt-3">
        <span className="text-xs bg-[#EF4444]/20 text-[#F87171] px-2 py-1 rounded font-medium">
          DECLINED
        </span>
      </div>
    );
  }

  const currentIdx = steps.indexOf(currentStatus);

  return (
    <div className="flex items-center gap-1 mt-3">
      {steps.map((step, idx) => {
        const done = idx < currentIdx;
        const active = idx === currentIdx;
        const cfg = STATUS_CONFIG[step];
        return (
          <React.Fragment key={step}>
            <div
              className={`text-[10px] px-2 py-0.5 rounded font-medium whitespace-nowrap ${active
                  ? cfg.color + ' ring-1 ring-current'
                  : done
                    ? 'bg-[#10B981]/10 text-[#34D399]/60'
                    : 'bg-[#0F1117] text-gray-700 border border-gray-800'
                }`}
            >
              {cfg.label}
            </div>
            {idx < steps.length - 1 && (
              <span className={`text-xs ${done ? 'text-[#34D399]/40' : 'text-gray-800'}`}>›</span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function LoanDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [loan, setLoan] = useState<Loan | null>(null);
  const [loading, setLoading] = useState(true);
  const [rulesResult, setRulesResult] = useState<RulesResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  useEffect(() => {
    if (!id) return;
    api.get(`/loans/${id}`)
      .then(res => {
        setLoan(res.data);
        setNewStatus(res.data.status);
        if (res.data.rulesResult) {
          setRulesResult(res.data.rulesResult as RulesResult);
        }
      })
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleRulesResult = (result: RulesResult) => {
    setRulesResult(result);
  };

  const handleUpdate = async (data: any) => {
    if (!loan) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/loans/${loan.id}`, data);
      setLoan(res.data);
      if (res.data.rulesResult) setRulesResult(res.data.rulesResult as RulesResult);
      setMode('view');
      showToast('Loan updated successfully');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!loan || newStatus === loan.status) return;
    setStatusUpdating(true);
    try {
      const res = await api.put(`/loans/${loan.id}/status`, { status: newStatus });
      setLoan(res.data);
      showToast(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Status update failed');
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1117]">
        <NavBar />
        <div className="flex items-center justify-center py-32">
          <div className="text-gray-500">Loading loan...</div>
        </div>
      </div>
    );
  }

  if (!loan) return null;

  const result = rulesResult as any;
  const exceptions = result?.exceptions || [];
  const statusCfg = STATUS_CONFIG[loan.status] || { label: loan.status, color: 'bg-gray-700 text-gray-400' };
  const displayTitle = loan.sponsorName || loan.entityName || loan.productType || 'Loan Detail';

  return (
    <div className="min-h-screen bg-[#0F1117]">
      <NavBar />

      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#10B981] text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg">
          ✅ {toast}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
              >
                ← Pipeline
              </button>
              <span className="text-gray-700">/</span>
              <h1 className="text-lg font-semibold text-white">{displayTitle}</h1>
              <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusCfg.color}`}>
                {statusCfg.label}
              </span>
            </div>
            {/* Pipeline progress bar */}
            <StatusProgressBar currentStatus={loan.status} />
          </div>

          <div className="flex items-center gap-3 mt-1">
            {/* Reviewer: status dropdown + update button */}
            {user?.role === 'admin' && (
              <div className="flex items-center gap-2">
                <select
                  value={newStatus}
                  onChange={e => setNewStatus(e.target.value)}
                  className="bg-[#1A1D27] border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-[#2563EB]"
                >
                  {LOAN_STATUSES.map(s => (
                    <option key={s} value={s}>
                      {STATUS_CONFIG[s]?.label || s}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleStatusUpdate}
                  disabled={statusUpdating || newStatus === loan.status}
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm px-3 py-1.5 rounded-lg transition-colors"
                >
                  {statusUpdating ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            )}

            {/* Originator: edit mode toggle */}
            {user?.role === 'loan_originator' && loan.createdBy === user.id && (
              <button
                onClick={() => setMode(mode === 'view' ? 'edit' : 'view')}
                className={`text-sm px-3 py-1.5 rounded-lg transition-colors border ${mode === 'edit'
                    ? 'border-[#F59E0B] text-[#FBBF24] bg-[#F59E0B]/10'
                    : 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-600'
                  }`}
              >
                {mode === 'edit' ? 'Cancel Edit' : 'Edit'}
              </button>
            )}
          </div>
        </div>

        {/* Loan metadata strip */}
        <div className="bg-[#1A1D27] rounded-xl p-4 border border-gray-800 mb-6 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Product</div>
            <div className="text-white text-sm font-medium mt-0.5">{loan.productType || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">State</div>
            <div className="font-mono text-white font-medium mt-0.5">{loan.state || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Property</div>
            <div className="text-white text-sm mt-0.5">{loan.existingPropertyType || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Loan Amount</div>
            <div className="font-mono text-white font-medium mt-0.5">
              {loan.loanAmount != null ? '$' + loan.loanAmount.toLocaleString() : '—'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">FICO / Exp</div>
            <div className="font-mono text-white font-medium mt-0.5">
              {loan.avgFICO ?? '—'} / {loan.yearsExperience != null ? `${loan.yearsExperience}yr` : '—'}
            </div>
          </div>
          {result?.borrowerTier && (
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Tier</div>
              <div className={`font-mono text-sm font-semibold mt-0.5 ${result.borrowerTier === 'Tier1' ? 'text-[#34D399]' :
                  result.borrowerTier === 'Tier2' ? 'text-[#FBBF24]' : 'text-[#F87171]'
                }`}>
                {result.borrowerTier}
              </div>
            </div>
          )}
          <div className="ml-auto text-right">
            <div className="text-xs text-gray-500">Originator</div>
            <div className="text-sm text-gray-300 mt-0.5">{loan.user?.name}</div>
            <div className="text-xs text-gray-600">{new Date(loan.createdAt).toLocaleDateString()}</div>
          </div>
        </div>

        {/* Main layout */}
        <div className="grid grid-cols-5 gap-6">
          {/* Left: Form — convert interestRate from DB decimal to display percent */}
          <div className="col-span-3">
            <LoanForm
              defaultValues={{
                ...loan,
                interestRate: loan.interestRate != null ? loan.interestRate * 100 : undefined
              }}
              onRulesResult={handleRulesResult}
              onSubmit={handleUpdate}
              submitting={submitting}
              readOnly={mode === 'view'}
              rulesResult={rulesResult}
            />
          </div>

          {/* Right: Live results */}
          <div className="col-span-2">
            <div className="sticky top-4 space-y-3">
              <ExceptionPanel exceptions={exceptions} />
              <RatioPanel result={rulesResult} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
